/**
 * Acceso tipado a los endpoints de Feature 001. Cada función es un wrapper
 * delgado sobre `apiFetch<T>()`. Si aparece OpenAPI codegen, este archivo se
 * puede reemplazar sin tocar a los consumidores.
 */

import { apiFetch, ApiError, apiUpload, getApiBaseUrl } from './api'
import type {
  AssetKind,
  AttemptCounts,
  AttemptStarted,
  Client,
  ClientGalleriesListResponse,
  EventRankingsResponse,
  PublicRankingResponse,
  ClientGallery,
  ClientGalleryInput,
  ClientWithUsers,
  CreateClientRequest,
  CreateClientResponse,
  CreateEventRequest,
  CreateInvitationRequest,
  CreateOperatorRequest,
  CreateTriviaQuestionRequest,
  CreateUserRequest,
  CreateWheelSegmentRequest,
  CreateDeviceRequest,
  Device,
  DeviceType,
  Event,
  PairByCodeResponse,
  EventPublicInfoResponse,
  HeartbeatResponse,
  ImportQuestionsResponse,
  Invitation,
  InvitationByTokenResponse,
  MobileUploadAcceptedResponse,
  MobileUploadResponse,
  ModuleAsset,
  ModuleAssetsListResponse,
  Operator,
  Paginated,
  PhotoboothAsset,
  PhotoboothAssetKind,
  PhotoboothAssetsResponse,
  PhotoboothPhoto,
  PhotoboothPhotosListResponse,
  PhotoboothRejectedListResponse,
  PhotoboothRejectedUpload,
  PostScoreResponse,
  PublicGalleryResponse,
  ScoresResponse,
  BatchSendSummary,
  CheckInResponse,
  CheckInsListResponse,
  DjConsoleQueueResponse,
  DjConsoleSession,
  DjConsoleState,
  DjHistoryResponse,
  DjQueueItem,
  DjQueueResponse,
  DjSearchResult,
  DjSongRequest,
  MessageMarkableStatus,
  MessagesListResponse,
  OutboundMessage,
  OutboundMessageKind,
  OutboundMessageStatus,
  SeatingSearchResponse,
  SessionSettings,
  SlotsCatalogResponse,
  SystemCapabilities,
  TriviaQuestion,
  TriviaQuestionsListResponse,
  UpdateClientRequest,
  UpdateDeviceRequest,
  UpdateEventRequest,
  UpdateInvitationRequest,
  UpdateOperatorRequest,
  UpdateTriviaQuestionRequest,
  UpdateUserRequest,
  UpdateWheelSegmentRequest,
  UserPublic,
  WheelSegment,
  WheelSegmentsListResponse,
  WheelSpinRequest,
  WheelSpinResponse,
  WheelWinsListResponse,
  // F028 §5.7
  AssignPersonsToInvitationRequest,
  CreatePersonRequest,
  DietaryRestrictions,
  ListPersonsQuery,
  ListPersonsResponse,
  Person,
  PersonInInvitationContext,
  UpdateAttendanceByTokenRequest,
  UpdatePersonRequest,
  // F032 Fase 2
  AsyncJob,
  // F031 Fase 2
  CreateGuestNotificationRequest,
  GuestNotification,
  GuestNotificationsListResponse,
  PublicGuestNotification,
  PublicGuestNotificationsListResponse
} from '../types/api'

export const clientsApi = {
  list(params: { page?: number; page_size?: number; active?: boolean } = {}) {
    return apiFetch<Paginated<Client>>('/clients', { query: params })
  },
  /**
   * El cliente logueado leyendo su propio cliente. Necesario para que la
   * consola de cliente sepa su cuota y sus `enabled_modules` (Feature 001
   * §5.4). El endpoint `/clients/me` no está listado explícito en §4.4 — se
   * asume que el backend lo expone scopeado al JWT.
   */
  me() {
    return apiFetch<Client>('/clients/me')
  },
  get(id: string) {
    return apiFetch<ClientWithUsers>(`/clients/${id}`)
  },
  create(body: CreateClientRequest) {
    return apiFetch<CreateClientResponse>('/clients', { method: 'POST', body })
  },
  update(id: string, body: UpdateClientRequest) {
    return apiFetch<Client>(`/clients/${id}`, { method: 'PATCH', body })
  },
  remove(id: string) {
    return apiFetch<void>(`/clients/${id}`, { method: 'DELETE' })
  },
  addUser(id: string, body: CreateUserRequest) {
    return apiFetch<UserPublic>(`/clients/${id}/users`, { method: 'POST', body })
  }
}

export const eventsApi = {
  list(
    params: { client_id?: string; archived?: boolean; page?: number; page_size?: number } = {}
  ) {
    return apiFetch<Paginated<Event>>('/events', { query: params })
  },
  get(id: string) {
    return apiFetch<Event>(`/events/${id}`)
  },
  create(body: CreateEventRequest) {
    return apiFetch<Event>('/events', { method: 'POST', body })
  },
  update(id: string, body: UpdateEventRequest) {
    return apiFetch<Event>(`/events/${id}`, { method: 'PATCH', body })
  },
  /**
   * Toggle dedicado del flag `events.invitations_active` (F007 §4.2 +
   * `internal/invitations/routes.go:19` del backend). Endpoint separado
   * de `PATCH /events/:id` porque el handler general no acepta
   * `invitations_active` en el body — devuelve `invalid json body`.
   * Acá pegamos al handler dedicado que sí lo acepta.
   *
   * Devuelve la projection pública del evento con el flag actualizado,
   * mismo shape que `update`.
   */
  toggleInvitations(eventId: string, invitations_active: boolean) {
    return apiFetch<Event>(`/events/${eventId}/invitations-toggle`, {
      method: 'PATCH',
      body: { invitations_active }
    })
  },
  /**
   * F007c §2.2: renderiza el mensaje de invitación (apodo + evento + link +
   * template custom si hay) **sin requerir teléfono y sin escribir en
   * `outbound_message_log`** — es un preview ephemeral. Lo usa el botón
   * "Copiar mensaje" para los invitados sin teléfono (y opcionalmente para
   * todos). Auth: super_admin / client owner.
   */
  messagePreview(eventId: string, invitationId: string) {
    return apiFetch<{ rendered: string }>(
      `/events/${encodeURIComponent(eventId)}/invitations/${encodeURIComponent(invitationId)}/message-preview`
    )
  },
  /**
   * F030 §4.2: publica el evento (`POST /events/:id/confirm`). Consume 1
   * crédito de la cuota del cliente (`event_quota`) y congela `name`,
   * `starts_at`, `ends_at` para client/operator. Auth: super_admin / client
   * owner (operator NO). Devuelve el `Event` actualizado (mismo shape que
   * `get`/`update`) con `confirmed_at` ya seteado.
   *
   * Errores del backend (mapeados por `code` en el caller):
   *  - `409 quota_exceeded` con `details: { quota, used }` — cuota llena.
   *  - `409 already_confirmed` — ya estaba publicado.
   *  - `400 incomplete_event` con `details: { field }` (name|starts_at|ends_at).
   */
  confirm(eventId: string) {
    return apiFetch<Event>(`/events/${encodeURIComponent(eventId)}/confirm`, {
      method: 'POST'
    })
  },
  /**
   * F030 §4.4: despublica el evento (`POST /events/:id/unconfirm`). Vuelve a
   * borrador (`confirmed_at = null`), devuelve el crédito y reabre la edición
   * de nombre/fechas. **Solo super_admin** — client/operator reciben 404.
   * Devuelve el `Event` actualizado.
   */
  unconfirm(eventId: string) {
    return apiFetch<Event>(`/events/${encodeURIComponent(eventId)}/unconfirm`, {
      method: 'POST'
    })
  },
  /**
   * Toggle dedicado de un módulo (kill-switch del operador, F004).
   * Endpoint separado de `PATCH /events/:id` para que un operator
   * asignado pueda activar/desactivar módulos en vivo sin tener permiso
   * sobre el resto del evento (el PATCH general es admin/client only).
   *
   * Response shape: solo `{ modules: string[] }` — NO devuelve el `Event`
   * completo. Los componentes que usen este método tienen que hacer un
   * merge parcial (solo actualizar `event.modules`) y conservar los demás
   * campos del state local.
   *
   * Errores específicos del backend:
   *  - `409 dj_phone_required` al activar `dj` sin `dj_phone_number`.
   *  - `400 invalid_slug` slug que no está en el catálogo.
   *  - `404 not_found` operator no asignado al evento.
   */
  toggleModule(eventId: string, slug: string, enabled: boolean) {
    return apiFetch<{ modules: string[] }>(
      `/events/${encodeURIComponent(eventId)}/modules/${encodeURIComponent(slug)}/toggle`,
      { method: 'POST', body: { enabled } }
    )
  },
  archive(id: string) {
    return apiFetch<void>(`/events/${id}`, { method: 'DELETE' })
  },
  /**
   * Genera (o devuelve) el token público de la PWA del DJ
   * (`POST /events/:id/dj/access-token`). Idempotente con `rotate=false`
   * (devuelve el existente). `rotate=true` fuerza uno nuevo e invalida el
   * anterior — el link viejo deja de funcionar. Solo super_admin / client.
   */
  generateDjToken(eventId: string, rotate = false) {
    const qs = rotate ? '?rotate=true' : ''
    return apiFetch<{ dj_token: string }>(
      `/events/${encodeURIComponent(eventId)}/dj/access-token${qs}`,
      { method: 'POST' }
    )
  },
  uploadAsset(
    id: string,
    kind: AssetKind,
    file: File,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
  ) {
    const fd = new FormData()
    fd.append('file', file)
    const xhrOpts: Parameters<typeof apiUpload>[2] = {}
    if (opts.onProgress) xhrOpts.onProgress = opts.onProgress
    if (opts.signal) xhrOpts.signal = opts.signal
    return apiUpload<Event>(`/events/${id}/assets/${kind}`, fd, xhrOpts)
  },
  deleteAsset(id: string, kind: AssetKind) {
    return apiFetch<void>(`/events/${id}/assets/${kind}`, { method: 'DELETE' })
  },
  async listOperators(id: string) {
    const res = await apiFetch<{ items: Operator[] }>(`/events/${id}/operators`)
    return res.items
  },
  async assignableOperators(id: string) {
    const res = await apiFetch<{ items: Operator[] }>(`/events/${id}/assignable-operators`)
    return res.items
  },
  assignOperator(id: string, operatorId: string) {
    return apiFetch<void>(`/events/${id}/operators`, {
      method: 'POST',
      body: { operator_id: operatorId }
    })
  },
  unassignOperator(id: string, operatorId: string) {
    return apiFetch<void>(`/events/${id}/operators/${operatorId}`, { method: 'DELETE' })
  },
  async listDevices(id: string) {
    const res = await apiFetch<{ items: Device[] }>(`/events/${id}/devices`)
    return res.items
  }
}

// ─── F005: Usuarios (PATCH transversal) ──────────────────────────────────────

export const usersApi = {
  /**
   * Edita un user existente (F005 §4). El caller arma el diff — solo incluye
   * los campos que cambiaron. `email`, `role` y `client_id` son inmutables en
   * el backend (400 si se mandan).
   */
  update(id: string, body: UpdateUserRequest) {
    return apiFetch<UserPublic>(`/users/${id}`, { method: 'PATCH', body })
  }
}

// ─── F004: Operadores ────────────────────────────────────────────────────────

export const operatorsApi = {
  list(params: { client_id?: string; page?: number; page_size?: number } = {}) {
    return apiFetch<Paginated<Operator>>('/operators', { query: params })
  },
  get(id: string) {
    return apiFetch<Operator>(`/operators/${id}`)
  },
  create(body: CreateOperatorRequest) {
    return apiFetch<Operator>('/operators', { method: 'POST', body })
  },
  update(id: string, body: UpdateOperatorRequest) {
    return apiFetch<Operator>(`/operators/${id}`, { method: 'PATCH', body })
  },
  remove(id: string, opts: { force?: boolean } = {}) {
    const options: Parameters<typeof apiFetch>[1] = { method: 'DELETE' }
    if (opts.force) options.query = { force: true }
    return apiFetch<void>(`/operators/${id}`, options)
  },
  /**
   * Endpoint que el operador logueado llama para listar sus eventos asignados
   * (F004 §4.1). Incluye `kiosk_pin` en cada evento. El backend responde con
   * el envelope `{ items: Event[] }`; acá se desempaca para devolver el array
   * directo a los consumidores.
   */
  async myEvents() {
    const res = await apiFetch<{ items: Event[] }>('/operators/me/events')
    return res.items
  }
}

// ─── F004 + F022: Devices unificados ─────────────────────────────────────────

/**
 * `devicesApi` post-F022: tabla unificada `devices` con `type` discriminator
 * (`totem` | `gallery`). Reemplaza `clientGalleriesApi` (que se mantiene
 * como wrapper de compat hasta F022.7 cleanup).
 *
 * Endpoints:
 *  - `pairByCode` (público, sin auth) — el técnico en venue escribe el code.
 *  - `create` / `update` / `unpair` / `remove` (JWT super_admin o cliente owner).
 *  - `list` (JWT) — con filtros opcionales por evento/cliente/type.
 *  - `heartbeat` (X-Device-Token) — sin cambios desde F004.
 *  - `slots` — catálogo del backend, sin cambios.
 */
export const devicesApi = {
  slots() {
    return apiFetch<SlotsCatalogResponse>('/devices/slots')
  },
  /**
   * Lista devices con filtros opcionales (F022 §5.7). El backend acepta
   * combinar `event_id`, `client_id` y `type` — se aplican como AND.
   */
  async list(opts: { event_id?: string; client_id?: string; type?: DeviceType } = {}) {
    const query: Record<string, string> = {}
    if (opts.event_id) query['event_id'] = opts.event_id
    if (opts.client_id) query['client_id'] = opts.client_id
    if (opts.type) query['type'] = opts.type
    const res = await apiFetch<{ items: Device[] }>('/devices', { query })
    return res.items
  },
  get(id: string) {
    return apiFetch<Device>(`/devices/${id}`)
  },
  /**
   * Crea un device pre-armado (F022 §4.4). El backend genera `public_id`
   * 6 chars base62 random + `kiosk_pin` 4 chars si no viene.
   */
  create(body: CreateDeviceRequest) {
    return apiFetch<{ device: Device }>('/devices', { method: 'POST', body })
  },
  update(id: string, body: UpdateDeviceRequest) {
    return apiFetch<Device>(`/devices/${id}`, { method: 'PATCH', body })
  },
  /**
   * Desacopla un device (F022 §4.3): invalida el `device_token_hash` para
   * que el próximo heartbeat del device físico reciba 410 y vuelva al
   * `<PairingByCodeFlow>`. La row se conserva con su config (label, slot,
   * theme, kiosk_pin) — el cliente puede re-parear con el mismo `public_id`.
   *
   * Auth: JWT super_admin / cliente owner / operator asignado.
   */
  unpair(id: string) {
    return apiFetch<{ device: Device }>(`/devices/${id}/unpair`, {
      method: 'POST'
    })
  },
  remove(id: string) {
    return apiFetch<void>(`/devices/${id}`, { method: 'DELETE' })
  },
  /**
   * Pairing público por código (F022 §4.2). Sin auth. El backend valida
   * `code` formato `^[A-Za-z0-9]{6}$`, lookup en `devices` con `type='totem'`
   * no pareado, genera secret `device_token` y devuelve device + evento
   * resuelto.
   *
   * `skipAuthHandling: true` para que un 401 imposible no haga logout de
   * un usuario eventualmente logueado en otra tab.
   *
   * Errores posibles (ver §4.2):
   *  - `400 invalid_code_format`
   *  - `404 device_not_found`
   *  - `400 device_type_invalid` (gallery no se pairea)
   *  - `409 already_paired`
   *  - `410 device_inactive`
   */
  pairByCode(code: string) {
    return apiFetch<PairByCodeResponse>('/devices/by-pairing-code', {
      method: 'POST',
      body: { code },
      skipAuthHandling: true
    })
  },
  /**
   * Heartbeat del dispositivo (F004 §4.3, sin cambios estructurales en
   * F022). Manda el token opaco en `X-Device-Token`. Backend responde 410
   * Gone si el token está invalidado (post-unpair, soft-delete, o device
   * inactive) — el cliente HTTP traduce a `ApiError` con `code =
   * 'device_token_invalid'`.
   */
  heartbeat(deviceToken: string) {
    return apiFetch<HeartbeatResponse>('/devices/heartbeat', {
      method: 'POST',
      deviceToken
    })
  }
}

// ─── F006/F018: Módulo Recepción (seating) ─────────────────────────────────
//
// F028: el CRUD de seating (list, upload CSV, remove, downloadCsv) se
// eliminó — ahora vive bajo `personsApi` (modelo unificado). Solo
// queda `search`, que es el único endpoint que el slot Recepción del
// tótem necesita (F018 §5.1). El backend mantiene `/events/:id/seating/search`
// como adapter sobre `event_persons`.

export const seatingApi = {
  /**
   * Búsqueda live del tótem (F006 §5.5 + F018 §5.1). Acepta JWT o token
   * de device (vía `X-Device-Token`). Si `q` tiene menos de 2 caracteres
   * el backend igual responde 200 con `items: []` — el frontend corta
   * antes para evitar el roundtrip.
   */
  search(eventId: string, q: string, deviceToken?: string) {
    const opts: Parameters<typeof apiFetch>[1] = { query: { q } }
    if (deviceToken) opts.deviceToken = deviceToken
    return apiFetch<SeatingSearchResponse>(`/events/${eventId}/seating/search`, opts)
  }
}

// ─── F007: Invitaciones + PWA del invitado ──────────────────────────────────

/**
 * CRUD autenticado y endpoints públicos de F007. Los autenticados van bajo
 * `/events/:id/invitations/*` (super_admin + client). Los públicos
 * (`byToken`, `eventPublicInfo`) no llevan auth — el token viaja en el path.
 *
 * El toggle `invitations_active` se setea con `eventsApi.update` mandando
 * `{ invitations_active }` (campo nuevo del PATCH; ver `UpdateEventRequest`).
 * No hay endpoint dedicado — el backend lo expone via PATCH del evento.
 */
/** Slug del link público del invitado. Ver `lib/guestLinks.ts`. */
export const publicSlugApi = {
  /**
   * PUT y no PATCH del evento porque cambiarlo no es editar un campo: el slug
   * anterior queda como histórico y su link sigue resolviendo. `409
   * slug_taken` si lo usa otro evento.
   */
  set(eventId: string, slug: string) {
    return apiFetch<{ public_slug: string }>(
      `/events/${encodeURIComponent(eventId)}/public-slug`,
      { method: 'PUT', body: { public_slug: slug } }
    )
  }
}

export const invitationsApi = {
  list(
    eventId: string,
    params: { page?: number; page_size?: number; q?: string } = {}
  ) {
    return apiFetch<Paginated<Invitation>>(`/events/${eventId}/invitations`, {
      query: params
    })
  },
  get(eventId: string, invitationId: string) {
    return apiFetch<Invitation>(`/events/${eventId}/invitations/${invitationId}`)
  },
  create(eventId: string, body: CreateInvitationRequest) {
    return apiFetch<Invitation>(`/events/${eventId}/invitations`, {
      method: 'POST',
      body
    })
  },
  update(eventId: string, invitationId: string, body: UpdateInvitationRequest) {
    return apiFetch<Invitation>(`/events/${eventId}/invitations/${invitationId}`, {
      method: 'PATCH',
      body
    })
  },
  /**
   * Genera un token nuevo para una invitación existente (F007 §4.2). El viejo
   * empieza a devolver 410 inmediatamente. Útil cuando el invitado compartió
   * el link de más.
   */
  regenerate(eventId: string, invitationId: string) {
    return apiFetch<Invitation>(
      `/events/${eventId}/invitations/${invitationId}/regenerate`,
      { method: 'POST' }
    )
  },
  /** Soft delete: setea `revoked_at = NOW()`. Endpoint público devuelve 410. */
  revoke(eventId: string, invitationId: string) {
    return apiFetch<Invitation>(
      `/events/${eventId}/invitations/${invitationId}/revoke`,
      { method: 'POST' }
    )
  },
  /** Reactiva una revocada: vuelve `revoked_at` a NULL. */
  restore(eventId: string, invitationId: string) {
    return apiFetch<Invitation>(
      `/events/${eventId}/invitations/${invitationId}/restore`,
      { method: 'POST' }
    )
  },
  /** Hard delete. Solo super admin (F007 §4.2) — el cliente debe usar revoke. */
  remove(eventId: string, invitationId: string) {
    return apiFetch<void>(`/events/${eventId}/invitations/${invitationId}`, {
      method: 'DELETE'
    })
  },
  // F028: `upload` (CSV import legacy de invitations) y `downloadCsv`
  // (export CSV legacy) se eliminaron. El import/export ahora es
  // unificado y async (F032 Fase 2) vía `personsApi.importAsync` /
  // `personsApi.exportAsync` — el backend deduplica por
  // `(name, invitation_id)` y encola el trabajo (poll vía `jobsApi.get`).
  /**
   * URL del PNG del QR (F007 §4.6). Devuelve la URL absoluta completa para
   * que el caller la use como `<img src>` o como destino de descarga.
   * Tamaño 512×512. El backend genera on-the-fly, sin caching.
   */
  qrPngUrl(eventId: string, invitationId: string): string {
    return `${getApiBaseUrl()}/events/${eventId}/invitations/${invitationId}/qr.png`
  },
  /**
   * Endpoint público (F007 §4.3). Sin auth — el token viaja en el path. El
   * backend devuelve 410 con mensaje genérico cuando el token está revocado,
   * el evento expiró, o `invitations_active=false` (no se leakea cuál).
   *
   * `skipAuthHandling: true` para que un eventual 401 no haga logout de un
   * usuario logueado — la PWA del invitado es independiente del auth store.
   *
   * `noCache: true` (defensa en profundidad sobre el `Cache-Control` que
   * ya manda el backend): el browser bypass-ea su HTTP cache para esta
   * request. Cubre el caso "invitado tenía cacheada respuesta vieja
   * pre-fix de cache backend" — sin esto, su PWA seguiría viendo la
   * versión cacheada hasta que expire por heurística.
   */
  byToken(token: string) {
    return apiFetch<InvitationByTokenResponse>(
      `/invitations/by-token/${encodeURIComponent(token)}`,
      { skipAuthHandling: true, noCache: true }
    )
  },
  /**
   * Endpoint público modo B (F007 §4.3). Solo responde si el evento NO tiene
   * el módulo `invitations` habilitado y `invitations_active=true`. En modo
   * A → 410.
   *
   * `noCache: true` por la misma razón que `byToken` — defensa en
   * profundidad para que cambios del cliente lleguen al invitado sin
   * delay de cache stale.
   */
  /**
   * Modo B por link legible (`/i/<slug>`). Misma respuesta que
   * `eventPublicInfo`, más `canonical_slug` cuando el slug usado es uno viejo
   * (el evento se renombró después de repartir el QR).
   */
  eventPublicInfoBySlug(slug: string) {
    // Ojo: el path del API NO espeja el de la página (`/i/<slug>`). Si lo
    // hiciera, con front y back en el mismo origen el proxy se llevaría la
    // navegación al backend y el invitado no vería la invitación.
    return apiFetch<EventPublicInfoResponse>(
      `/events/by-slug/${encodeURIComponent(slug)}/public-info`,
      { skipAuthHandling: true, noCache: true }
    )
  },

  eventPublicInfo(eventId: string) {
    return apiFetch<EventPublicInfoResponse>(
      `/events/${encodeURIComponent(eventId)}/public-info`,
      { skipAuthHandling: true, noCache: true }
    )
  },
  /**
   * Sube una foto del invitado al photobooth mobile (F007 §4.5 + F013 §4.1).
   *
   * Endpoint: `POST /events/:id/modules/photobooth/uploads` con
   * `X-Invitation-Token`. El backend extrae `player_name` del
   * `invitation.unit_name` si el body lo deja vacío.
   *
   * Validación clientside (acortada del backend para no perder roundtrip
   * obvio):
   *   - MIME ∈ { image/jpeg, image/png, image/webp } — backend rechaza GIF
   *     y HEIC/HEIF con 400 `invalid_mime`.
   *   - Tamaño ≤ 10 MiB — backend rechaza con 400 `size_exceeded`.
   *
   * Response 202 con shape `MobileUploadAcceptedResponse` — invariable,
   * agnóstico al resultado real del worker NSFW (F013 §5.2 / F007 §4.5).
   */
  uploadPhotobooth(
    eventId: string,
    invitationToken: string,
    blob: Blob,
    opts: {
      playerName?: string
      onProgress?: (fraction: number) => void
      signal?: AbortSignal
    } = {}
  ): Promise<MobileUploadAcceptedResponse> {
    const file =
      blob instanceof File
        ? blob
        : new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' })
    if (!INVITATION_PHOTOBOOTH_ALLOWED_MIMES.includes(file.type)) {
      throw new ApiError({
        code: 'invalid_mime',
        message: `MIME no soportado: ${file.type || 'desconocido'}`,
        status: 400,
        details: { allowed: INVITATION_PHOTOBOOTH_ALLOWED_MIMES }
      })
    }
    if (file.size > INVITATION_PHOTOBOOTH_MAX_BYTES) {
      throw new ApiError({
        code: 'size_exceeded',
        message: 'El archivo supera el máximo permitido (10 MiB).',
        status: 400,
        details: { max: INVITATION_PHOTOBOOTH_MAX_BYTES }
      })
    }
    const fd = new FormData()
    fd.append('file', file)
    const trimmed = opts.playerName?.trim()
    if (trimmed) fd.append('player_name', trimmed)
    const xhrOpts: Parameters<typeof apiUpload>[2] = { invitationToken }
    if (opts.onProgress) xhrOpts.onProgress = opts.onProgress
    if (opts.signal) xhrOpts.signal = opts.signal
    return apiUpload<MobileUploadAcceptedResponse>(
      `/events/${encodeURIComponent(eventId)}/modules/photobooth/uploads`,
      fd,
      xhrOpts
    )
  }
}

/**
 * Subset del MIME whitelist del backend (`photobooth.AllowedMobileUploadMIMEs`,
 * F007 §4.5). Mantener en sync con la lista del backend si cambia.
 */
const INVITATION_PHOTOBOOTH_ALLOWED_MIMES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/webp'
]
const INVITATION_PHOTOBOOTH_MAX_BYTES = 10 * 1024 * 1024

// ─── F028: event_persons (modelo unificado personas + dietary) ──────────

/**
 * `personsApi` — endpoints CRUD + import/export para `event_persons`
 * (F028 §5.7). Reemplaza a `seatingApi` + parte de `invitationsApi`
 * (members JSONB) que quedan deprecated post-F028.
 *
 * Auth: super_admin / client owner para la mayoría. La excepción es
 * `updateDietaryByToken`, que es público: el token de la invitación
 * valida que la persona pertenece a la unidad accesible por ese token.
 * Si la persona NO está en la unidad del token, backend devuelve 403.
 *
 * **Estado backend (2026-05-17)**: la spec F028 está definida y el
 * backend trabaja en paralelo. Los endpoints exactos quedan congelados
 * en el shape de §5.7 — el frontend asume que el backend implementa
 * estos paths cuando cierre su Fase 2. Mientras tanto, la consola
 * funciona contra mocks (ver `EventInvitadosTab` Fase 2).
 */
export const personsApi = {
  list(eventId: string, params: ListPersonsQuery = {}) {
    const query: Record<string, string | number | boolean | undefined> = {}
    if (params.q !== undefined) query['q'] = params.q
    if (params.table !== undefined) query['table'] = params.table
    if (params.group !== undefined) query['group'] = params.group
    if (params.invitation_id !== undefined) query['invitation_id'] = params.invitation_id
    if (params.with_invitation !== undefined) query['with_invitation'] = params.with_invitation
    if (params.dietary_tag !== undefined) query['dietary_tag'] = params.dietary_tag
    if (params.page !== undefined) query['page'] = params.page
    if (params.page_size !== undefined) query['page_size'] = params.page_size
    return apiFetch<ListPersonsResponse>(
      `/events/${encodeURIComponent(eventId)}/persons`,
      { query }
    )
  },
  // NOTA: el backend F028 devuelve la persona PELADA en create/update/get
  // (no envuelta en `{ person }`) — ver internal/persons/handler.go y los
  // tests de integración. Solo el endpoint público de attendance envuelve.
  get(personId: string) {
    return apiFetch<Person>(`/persons/${encodeURIComponent(personId)}`)
  },
  create(eventId: string, body: CreatePersonRequest) {
    return apiFetch<Person>(
      `/events/${encodeURIComponent(eventId)}/persons`,
      { method: 'POST', body }
    )
  },
  update(personId: string, body: UpdatePersonRequest) {
    return apiFetch<Person>(`/persons/${encodeURIComponent(personId)}`, {
      method: 'PATCH',
      body
    })
  },
  remove(personId: string) {
    return apiFetch<void>(`/persons/${encodeURIComponent(personId)}`, {
      method: 'DELETE'
    })
  },
  /**
   * F028 §5.7 + F032 Fase 2: import CSV unificado — ahora **async**. El
   * backend acepta columnas legacy de seating (`table_name`, `group_name`,
   * `name`) y de invitations (`unit_name`, `members`) y deduplica por
   * `(name, invitation_id)`. El `?force=true` borra todas las personas antes
   * de insertar; sin el flag es upsert.
   *
   * Devuelve `202 { job_id }` — el caller pollea `jobsApi.get(job_id)` hasta
   * `succeeded` y lee el `PersonsImportSummary` de `result`.
   */
  importAsync(eventId: string, file: File, opts: { force?: boolean } = {}) {
    const formData = new FormData()
    formData.append('file', file)
    const query = opts.force ? '?force=true' : ''
    return apiUpload<{ job_id: string }>(
      `/events/${encodeURIComponent(eventId)}/persons/import${query}`,
      formData
    )
  },
  /**
   * F028 §5.7 + F032 Fase 2: export CSV — ahora **async**. `POST` encola el
   * job y devuelve `202 { job_id }`; el caller pollea `jobsApi.get(job_id)`
   * hasta `succeeded` y descarga el `result.download_url` (presigned S3,
   * descarga directa sin auth header).
   */
  exportAsync(eventId: string) {
    return apiFetch<{ job_id: string }>(
      `/events/${encodeURIComponent(eventId)}/persons/export`,
      { method: 'POST' }
    )
  },
  /**
   * F028 §5.7: asigna N personas existentes a una invitación. Body
   * `{ person_ids: [...] }` no-vacío. Si alguna persona ya estaba asignada
   * a otra invitación, el backend devuelve 409 — el caller decide si
   * reintentar con `?force=true` (mueve la asignación).
   */
  assignToInvitation(
    eventId: string,
    invitationId: string,
    body: AssignPersonsToInvitationRequest
  ) {
    return apiFetch<{ assigned: number }>(
      `/events/${encodeURIComponent(eventId)}/invitations/${encodeURIComponent(invitationId)}/persons`,
      { method: 'POST', body }
    )
  },
  /**
   * F028 §5.7: endpoint público (sin auth — token en path). El backend
   * valida que `personId` pertenece a la unidad del `token`; si no, 403.
   * Mandar `null` limpia las restricciones. Devuelve la persona con el
   * shape del context invitación (sin notes, sin created_at).
   */
  updateDietaryByToken(
    token: string,
    personId: string,
    body: DietaryRestrictions | null
  ) {
    return apiFetch<PersonInInvitationContext>(
      `/invitations/by-token/${encodeURIComponent(token)}/persons/${encodeURIComponent(personId)}/dietary`,
      { method: 'PATCH', body, skipAuthHandling: true }
    )
  },
  /**
   * F028b §5.5: endpoint público (sin auth — token en path). El backend
   * valida que `personId` pertenece a la unidad del `token`. Acepta solo
   * `confirmed` o `declined` — `pending` devuelve 400 `invalid_status`.
   * Response: `{ person: PersonInInvitationContext }` con `attendance_status`
   * y `attendance_updated_at` actualizados.
   *
   * Errores esperados:
   *  - 400 `invalid_status` si se manda `pending` o un valor inválido.
   *  - 404 si person no pertenece a la invitación.
   *  - 410 si el token está revocado / la invitación expiró.
   */
  updateAttendanceByToken(
    token: string,
    personId: string,
    body: UpdateAttendanceByTokenRequest
  ) {
    return apiFetch<{ person: PersonInInvitationContext }>(
      `/invitations/by-token/${encodeURIComponent(token)}/persons/${encodeURIComponent(personId)}/attendance`,
      { method: 'PATCH', body, skipAuthHandling: true }
    )
  }
}

// ─── F032 Fase 2: estado de jobs async ───────────────────────────────────

/**
 * `jobsApi` — estado de jobs async (`GET /jobs/:id`, F032 §5). Auth JWT;
 * scopeado server-side (404 si el job no existe o cae fuera del scope del
 * requester — sin leak). El cliente lo pollea tras encolar un import/export
 * de invitados hasta un estado terminal (`succeeded|failed|canceled`).
 */
export const jobsApi = {
  get(jobId: string) {
    return apiFetch<AsyncJob>(`/jobs/${encodeURIComponent(jobId)}`)
  }
}

// ─── F007b: Sender + envío automatizado de invitaciones ──────────────────

/**
 * `messagingApi` — endpoints de envío + auditoría (F007b §4.8).
 *
 * Auth: super_admin / client owner (sin device token — el envío es acción
 * de consola). El cliente HTTP cae al JWT del `authStore` automáticamente.
 *
 * Sobre el adapter activo: el frontend NO conoce qué adapter está
 * configurado en el backend. Detecta por inspección del response: si
 * `message.wame_url !== null` → es `wame` (frontend abre URL en pestaña
 * nueva). Si `wame_url === null` y `status === 'sent'` → es `log_only` o
 * `twilio` (no hay nada que abrir, refrescar lista y listo).
 */
export const messagingApi = {
  /**
   * `POST /events/:id/invitations/:invitation_id/send` — dispara el
   * sender. Si la invitación no tiene `phone_number`, el backend devuelve
   * 400 `phone_required`. La consola previene esto deshabilitando el
   * botón "Enviar" cuando `phone_number === null`.
   */
  send(eventId: string, invitationId: string) {
    return apiFetch<{ message: OutboundMessage }>(
      `/events/${encodeURIComponent(eventId)}/invitations/${encodeURIComponent(invitationId)}/send`,
      { method: 'POST' }
    )
  },

  /**
   * `POST /events/:id/invitations/send-batch` — dispara una row por cada
   * invitación pendiente (con phone, sin envío previo en estado terminal).
   * El response trae `wame_urls` solo si el adapter es `wame` — la consola
   * abre el `<SendBatchModal>` para iterar sobre ellos manualmente.
   */
  sendBatch(eventId: string) {
    return apiFetch<BatchSendSummary>(
      `/events/${encodeURIComponent(eventId)}/invitations/send-batch`,
      { method: 'POST' }
    )
  },

  /**
   * `PATCH /events/:id/messages/:message_id` — el cliente confirma
   * (`manual_sent`) o revierte (`failed`) un envío manual. TS-limited a
   * los 2 valores que el frontend debe poder marcar — el resto del state
   * machine lo controla el provider.
   *
   * Backend devuelve `{ message: OutboundMessage }` (mismo wrapper que
   * `send`). Acá deserializamos para devolver el `OutboundMessage` directo
   * — la API del frontend queda más limpia (el caller no sabe del wrapper).
   */
  async markStatus(
    eventId: string,
    messageId: string,
    status: MessageMarkableStatus
  ): Promise<OutboundMessage> {
    const res = await apiFetch<{ message: OutboundMessage }>(
      `/events/${encodeURIComponent(eventId)}/messages/${encodeURIComponent(messageId)}`,
      { method: 'PATCH', body: { status } }
    )
    return res.message
  },

  /**
   * `GET /events/:id/messages` — auditoría paginada. Filtros opcionales
   * por `kind` y `status`. El header de `InvitationsConfigPage` pide los
   * últimos N mensajes con `kind: 'invitation'` y los agrupa por
   * `related_invitation_id` para derivar el badge de cada row. Si
   * `total > items.length`, la consola muestra un banner sugiriendo
   * abrir la vista `MessagesView` para ver el detalle completo.
   */
  list(
    eventId: string,
    opts: {
      kind?: OutboundMessageKind
      status?: OutboundMessageStatus
      limit?: number
      offset?: number
    } = {}
  ) {
    const params = new URLSearchParams()
    if (opts.kind) params.set('kind', opts.kind)
    if (opts.status) params.set('status', opts.status)
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts.offset !== undefined) params.set('offset', String(opts.offset))
    const qs = params.toString()
    return apiFetch<MessagesListResponse>(
      `/events/${encodeURIComponent(eventId)}/messages${qs ? `?${qs}` : ''}`
    )
  }
}

// ─── F031 Fase 2: Notificaciones al invitado (consola + feed) ───────────

/**
 * `guestNotificationsApi` — composición/programación desde la consola
 * (JWT) + feed público del invitado (F031 §4 / §7).
 *
 * **Consola** (`create`/`list`/`cancel`): JWT super_admin / cliente owner —
 * `apiFetch` cae al Bearer del `authStore` automáticamente.
 *
 * **Feed** (`feed`): PÚBLICO, sin JWT. Mismo patrón que el resto del
 * surface del invitado (ver `moduleScoresApi.listPublic`, `djApi.search`):
 *   - Modo A: header `X-Invitation-Token: <token>` → broadcasts + las
 *     dirigidas a esa invitación.
 *   - Modo B: sin token → solo broadcasts.
 *   - `skipAuthHandling: true` (un 401 no debe desloguear una sesión de
 *     consola abierta en otra tab) + `noCache: true` (datos frescos
 *     time-aware, igual que los demás endpoints públicos del invitado).
 */
export const guestNotificationsApi = {
  /**
   * `POST /events/:id/notifications` — crea la notificación. `fire_at`
   * ausente/null/pasado = enviar ahora; futuro = programar.
   * `target_invitation_id` null/ausente = broadcast. Devuelve la
   * notificación creada (shape consola).
   */
  create(
    eventId: string,
    body: CreateGuestNotificationRequest
  ): Promise<GuestNotification> {
    return apiFetch<GuestNotification>(
      `/events/${encodeURIComponent(eventId)}/notifications`,
      { method: 'POST', body }
    )
  },

  /**
   * `GET /events/:id/notifications` — lista de la consola. Trae todas las
   * notificaciones del evento (scheduled / sent / canceled) más recientes
   * primero. Se desempaca el envelope `{ items }`.
   */
  async list(eventId: string): Promise<GuestNotification[]> {
    const res = await apiFetch<GuestNotificationsListResponse>(
      `/events/${encodeURIComponent(eventId)}/notifications`
    )
    return res.items
  },

  /**
   * `DELETE /events/:id/notifications/:notification_id` — cancela una
   * programada futura. El backend devuelve 404/409 si ya pasó o no existe;
   * el caller mapea por `code` con un Toast.
   */
  cancel(eventId: string, notificationId: string): Promise<void> {
    return apiFetch<void>(
      `/events/${encodeURIComponent(eventId)}/notifications/${encodeURIComponent(notificationId)}`,
      { method: 'DELETE' }
    )
  },

  /**
   * `GET /events/:id/guest-notifications` — feed PÚBLICO del invitado.
   * Modo A: pasar `invitationToken` (viaja como `X-Invitation-Token`).
   * Modo B: sin token (solo broadcasts). Devuelve solo las notificaciones
   * ya disparadas y visibles para el invitado (server-side time-aware).
   */
  async feed(
    eventId: string,
    opts: { invitationToken?: string } = {}
  ): Promise<PublicGuestNotification[]> {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      skipAuthHandling: true,
      noCache: true
    }
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    const res = await apiFetch<PublicGuestNotificationsListResponse>(
      `/events/${encodeURIComponent(eventId)}/guest-notifications`,
      fetchOpts
    )
    return res.items
  }
}

// ─── F017: Pedir canción al DJ via PWA del invitado ─────────────────────

/**
 * `djApi` — endpoints del módulo DJ (F017).
 *
 * Auth dual en `search` y `submit` (igual patrón que F006 reception):
 *   - JWT (super_admin / client / operator) — sin uso típico, está
 *     habilitado para que el operador pueda probar el flow.
 *   - O `X-Invitation-Token` (invitado modo A).
 *   - O sin auth (invitado modo B con throttle por IP del backend).
 *
 * `queueList` y `history` son JWT-only (operador / cliente / super admin).
 *
 * **No hay `queueMark` dedicado**: la transición `manual_pending →
 * manual_sent | failed` reusa `messagingApi.markStatus` (ver F007b §4.8).
 * Asimetría deliberada con `queueList` (que sí es dedicado porque
 * proyecta `duplicate_count`, `total_pending`, `total_today` que el
 * endpoint genérico no provee).
 */
export const djApi = {
  /**
   * `GET /events/:id/dj/song-search?q=<query>&limit=<int>` — proxy
   * Deezer del backend. `invitationToken` opcional: cuando se pasa va
   * como `X-Invitation-Token` (modo A); sin él, request anónimo (modo B
   * con throttle por IP).
   */
  search(
    eventId: string,
    q: string,
    opts: { limit?: number } = {},
    invitationToken?: string
  ) {
    const params = new URLSearchParams({ q })
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      // Endpoint público — sin auto-logout en 401 (no aplica acá).
      skipAuthHandling: true
    }
    if (invitationToken) fetchOpts.invitationToken = invitationToken
    return apiFetch<{ results: DjSearchResult[] }>(
      `/events/${encodeURIComponent(eventId)}/dj/song-search?${params}`,
      fetchOpts
    )
  },

  /**
   * `GET /events/:id/dj/trending?limit=<int>` — los temas más escuchados
   * ahora (Deezer chart global). Sirven de sugerencias por defecto cuando
   * el invitado todavía no buscó nada; siempre actualizado, sin lista
   * hardcodeada. Mismo gate/auth dual que `search`.
   */
  trending(
    eventId: string,
    opts: { limit?: number } = {},
    invitationToken?: string
  ) {
    const params = new URLSearchParams()
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      skipAuthHandling: true
    }
    if (invitationToken) fetchOpts.invitationToken = invitationToken
    const qs = params.toString()
    return apiFetch<{ results: DjSearchResult[] }>(
      `/events/${encodeURIComponent(eventId)}/dj/trending${qs ? `?${qs}` : ''}`,
      fetchOpts
    )
  },

  /**
   * `POST /events/:id/dj/song-request` — submit del pedido. Backend crea
   * row en `outbound_message_log` con `kind='dj_request'`, invoca el
   * Sender, devuelve mensaje genérico al invitado (sin info del envío
   * real — coherente con F013 mobile photobooth).
   *
   * Errores específicos: `429 too_many_requests` (throttle 60s, header
   * `Retry-After`); `410 gone` (módulo deshabilitado o evento fuera de
   * ventana — copy genérico al invitado).
   */
  submit(
    eventId: string,
    request: DjSongRequest,
    invitationToken?: string
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      method: 'POST',
      body: request,
      skipAuthHandling: true
    }
    if (invitationToken) fetchOpts.invitationToken = invitationToken
    return apiFetch<{ status: string; message: string }>(
      `/events/${encodeURIComponent(eventId)}/dj/song-request`,
      fetchOpts
    )
  },

  /**
   * `GET /events/:id/dj/queue?status=<...>&limit=<int>` — cola del
   * operador. JWT auth. Devuelve items + `total_pending` + `total_today`.
   * El frontend usa polling cada 10s para refrescar.
   */
  queueList(
    eventId: string,
    opts: { status?: OutboundMessageStatus; limit?: number } = {}
  ) {
    const params = new URLSearchParams()
    if (opts.status) params.set('status', opts.status)
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    const qs = params.toString()
    return apiFetch<DjQueueResponse>(
      `/events/${encodeURIComponent(eventId)}/dj/queue${qs ? `?${qs}` : ''}`
    )
  },

  /**
   * `GET /events/:id/dj/history?limit=<int>&offset=<int>` — histórico
   * paginado para la consola del cliente (vista readonly). Incluye TODOS
   * los status, ordenado por `created_at DESC`.
   */
  history(eventId: string, opts: { limit?: number; offset?: number } = {}) {
    const params = new URLSearchParams()
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts.offset !== undefined) params.set('offset', String(opts.offset))
    const qs = params.toString()
    return apiFetch<DjHistoryResponse>(
      `/events/${encodeURIComponent(eventId)}/dj/history${qs ? `?${qs}` : ''}`
    )
  },

  /**
   * `POST /events/:id/dj/compile-pending` (extensión bulk send,
   * 2026-05-10) — el backend agrega TODOS los pedidos pendientes
   * (`status='manual_pending'`) en un único mensaje formateado y
   * devuelve la `wame_url` lista para abrir en WhatsApp + el listado
   * de `item_ids` que el frontend tiene que marcar como `manual_sent`
   * tras abrir el deeplink.
   *
   * Si entre la compile y el markStatus alguien marcó alguno (race
   * con otro rol que tenga la cola abierta), los `markStatus` que
   * fallen con 409 se ignoran silently — el caller usa
   * `Promise.allSettled` para no romper el batch.
   */
  compilePending(eventId: string) {
    return apiFetch<{
      wame_url: string
      rendered_message: string
      item_ids: string[]
      count: number
    }>(`/events/${encodeURIComponent(eventId)}/dj/compile-pending`, {
      method: 'POST'
    })
  }
}

// Re-export `DjQueueItem` para que los componentes que usan djApi no
// necesiten importarlo desde `types/api.ts` aparte. Patrón coherente con
// `messagingApi` (que comparte tipos via re-import desde el componente).
export type { DjQueueItem }

// ─── PWA del DJ — consola pública en vivo (auth por `?token=`) ────────────

/**
 * `djConsoleApi` — endpoints de la PWA pública del DJ (`/dj-console/*`).
 *
 * Auth por query param `?token=<dj_token>`, NO por JWT ni headers. Son
 * públicos: el DJ entra con un link sin login. `skipAuthHandling: true`
 * evita el auto-logout en un eventual 401 (no aplica, pero por las dudas
 * no tocamos la sesión del operador si tuviera una abierta en otra tab).
 *
 * `410 gone` cubre token inválido / evento archivado / módulo dj apagado —
 * la ruta lo trata como "link no disponible".
 */
export const djConsoleApi = {
  session(token: string) {
    return apiFetch<DjConsoleSession>(
      `/dj-console/session?token=${encodeURIComponent(token)}`,
      { skipAuthHandling: true }
    )
  },
  queue(token: string) {
    return apiFetch<DjConsoleQueueResponse>(
      `/dj-console/queue?token=${encodeURIComponent(token)}`,
      { skipAuthHandling: true }
    )
  },
  setState(token: string, requestId: string, state: DjConsoleState) {
    return apiFetch<{ ok: true; state: DjConsoleState }>(
      `/dj-console/requests/${encodeURIComponent(requestId)}/state?token=${encodeURIComponent(token)}`,
      { method: 'POST', body: { state }, skipAuthHandling: true }
    )
  }
}

// ─── F018: Validador QR de check-in en la entrada del evento ──────────────

/**
 * `checkInsApi` — endpoints de check-in via QR scan (F018).
 *
 * Auth dual:
 *   - JWT (super_admin / client owner / operator asignado).
 *   - O `X-Device-Token` de un device pareado al evento con slot `reception`.
 *
 * El parámetro `deviceToken` es opcional. Cuando se provee, `apiFetch` manda
 * `X-Device-Token` (sin Authorization Bearer) — mismo patrón que
 * `seatingApi.search` y `devicesApi.heartbeat`. Sin él, el cliente HTTP cae
 * al JWT del `authStore`.
 */
export const checkInsApi = {
  /**
   * `POST /events/:id/check-ins`. Body `{ invitation_token }`. UPSERT
   * incondicional: cada scan incrementa `check_in_count`. Re-scans reciben
   * `status: 'already_checked_in'` con count > 1.
   *
   * Errores específicos al staff: el endpoint NO devuelve 410 genérico; en su
   * lugar mapea a `ApiError.code` ∈ { `token_not_found`, `token_revoked`,
   * `event_expired`, `wrong_event`, `invitations_disabled`,
   * `device_not_authorized_for_check_in`, `invalid_token_format` }. El
   * componente `<ResultPhase>` decide el copy de cada caso.
   */
  scan(eventId: string, invitationToken: string, deviceToken?: string) {
    const opts: Parameters<typeof apiFetch>[1] = {
      method: 'POST',
      body: { invitation_token: invitationToken }
    }
    if (deviceToken) opts.deviceToken = deviceToken
    return apiFetch<CheckInResponse>(
      `/events/${encodeURIComponent(eventId)}/check-ins`,
      opts
    )
  },

  /**
   * `GET /events/:id/check-ins`. Auth solo JWT (no device token — la lectura
   * la hace el cliente desde la consola, no el tótem). `q` filtra por
   * `unit_name` ILIKE serverside.
   */
  list(
    eventId: string,
    opts: { limit?: number; offset?: number; q?: string } = {}
  ) {
    const params = new URLSearchParams()
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts.offset !== undefined) params.set('offset', String(opts.offset))
    if (opts.q) params.set('q', opts.q)
    const qs = params.toString()
    return apiFetch<CheckInsListResponse>(
      `/events/${encodeURIComponent(eventId)}/check-ins${qs ? `?${qs}` : ''}`
    )
  }
}

// ─── F008: Módulos genéricos (settings + assets + scores) ───────────────────

/**
 * `PATCH /events/:event_id/modules/:module_slug/settings` (F008 §5.1).
 * El shape del body es módulo-específico — para `games.memory` es
 * `MemorySettings`. El backend valida el shape y devuelve el settings final.
 */
export const moduleSettingsApi = {
  update<TBody, TRes = TBody>(eventId: string, moduleSlug: string, body: TBody) {
    return apiFetch<TRes>(`/events/${eventId}/modules/${moduleSlug}/settings`, {
      method: 'PATCH',
      body
    })
  }
}

/**
 * Assets del módulo (F008 §5.2). El editor del cliente sube vía `upload`; el
 * tótem hace `list` con su `deviceToken` para pre-cargar imágenes antes del
 * juego (slot del device debe matchear el prefix del módulo, validado server
 * side).
 */
export const moduleAssetsApi = {
  /**
   * GET assets. Auth dual (F021 §5.2): tótem usa `deviceToken`, mobile A
   * usa `invitationToken`, mobile B sin auth. Sin opts → JWT del store.
   */
  async list(
    eventId: string,
    moduleSlug: string,
    opts: { deviceToken?: string; invitationToken?: string } = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {}
    if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    const res = await apiFetch<ModuleAssetsListResponse>(
      `/events/${eventId}/modules/${moduleSlug}/assets`,
      fetchOpts
    )
    return res.items
  },
  upload(
    eventId: string,
    moduleSlug: string,
    file: File,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
  ) {
    const fd = new FormData()
    fd.append('file', file)
    const xhrOpts: Parameters<typeof apiUpload>[2] = {}
    if (opts.onProgress) xhrOpts.onProgress = opts.onProgress
    if (opts.signal) xhrOpts.signal = opts.signal
    return apiUpload<ModuleAsset>(
      `/events/${eventId}/modules/${moduleSlug}/assets`,
      fd,
      xhrOpts
    )
  },
  remove(eventId: string, moduleSlug: string, assetId: string) {
    return apiFetch<void>(
      `/events/${eventId}/modules/${moduleSlug}/assets/${assetId}`,
      { method: 'DELETE' }
    )
  }
}

/**
 * Scores del módulo (F008 §5.3). `create` lo llama el tótem con su
 * `deviceToken` post-juego; `list` puede llamarse con JWT (consola) o token de
 * device (overlay del juego).
 */
export const moduleScoresApi = {
  list(
    eventId: string,
    moduleSlug: string,
    opts: { limit?: number; deviceToken?: string; invitationToken?: string } = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {}
    if (opts.limit !== undefined) fetchOpts.query = { limit: opts.limit }
    if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    return apiFetch<ScoresResponse>(
      `/events/${eventId}/modules/${moduleSlug}/scores`,
      fetchOpts
    )
  },
  /**
   * Endpoint público de scores (F007 §4.3.1) — sin auth, gateado server side
   * por `invitations_active`, expiración del evento, y módulo `invitations`
   * **no** habilitado (es decir: solo modo B). En modo A → 410.
   *
   * Lo consume `<Ranking mode="B">` del invitado modo B; en modo A se sigue
   * usando `list` con `invitationToken`.
   *
   * `noCache: true` igual que los otros endpoints públicos del invitado —
   * los scores cambian a medida que el evento avanza, no querés ver
   * rankings stale.
   */
  listPublic(
    eventId: string,
    moduleSlug: string,
    opts: { limit?: number } = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      skipAuthHandling: true,
      noCache: true
    }
    if (opts.limit !== undefined) fetchOpts.query = { limit: opts.limit }
    return apiFetch<ScoresResponse>(
      `/events/${encodeURIComponent(eventId)}/public-scores/${encodeURIComponent(moduleSlug)}`,
      fetchOpts
    )
  },
  /**
   * POST score (F008 §5.3 + F021 §4.4). Auth dual: tótem manda
   * `deviceToken`, mobile A manda `invitationToken`, mobile B sin auth.
   *
   * F021 §4.4: el body acepta `attempt_id` opcional para correlacionar
   * con `event_play_attempts.id`. Tótem omite (backward compat).
   */
  create<TBody>(
    eventId: string,
    moduleSlug: string,
    body: TBody,
    opts: { deviceToken?: string; invitationToken?: string } | string = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      method: 'POST',
      body
    }
    if (typeof opts === 'string') {
      // Compat con la firma legacy `(eventId, slug, body, deviceToken)`.
      fetchOpts.deviceToken = opts
    } else {
      if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
      if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    }
    return apiFetch<PostScoreResponse>(
      `/events/${eventId}/modules/${moduleSlug}/scores`,
      fetchOpts
    )
  }
}

// ─── F021: Tracking de intentos de juego (PWA invitado) ────────────────────

/**
 * `gameAttemptsApi` — endpoints públicos del invitado para Memory / Trivia
 * / Puzzle / Wheel mobile (F021 §4).
 *
 * Auth dual:
 *  - `X-Invitation-Token`: modo A (estricto server-side por `invitation_id`).
 *  - Sin auth: modo B. Para `start` el body requiere `player_name`.
 *
 * `start` y `counts` van bajo el grupo público — el cliente HTTP no debe
 * hacer logout en 401 (`skipAuthHandling: true`). El backend está rate
 * limited (10 req/s, mismo bucket que F007 endpoints públicos).
 */
export const gameAttemptsApi = {
  /**
   * `POST /events/:id/modules/:slug/play/start` — abre un attempt. Persiste
   * la row inmediatamente (consume cupo aunque el invitado abandone). El
   * `attempt_id` se pasa después al `POST /scores` para correlacionar.
   *
   * Errores específicos (mapeados desde `ApiError.code`):
   *  - `409 attempts_exhausted` (modo A excedió límite — `details.used/max/phase`)
   *  - `410 window_not_open` / `event_not_started` / `event_ended` (fuera de ventana)
   *  - `410 module_not_active` (slug no en `event.modules`)
   *  - `410 phase_disabled_for_module` (cliente seteó `max_attempts_<phase>=0`)
   *  - `400 invalid_slug` (módulo sin `MobileEnabled` en catálogo)
   *
   * `body.player_name`: solo modo B. Modo A lo ignora — el backend toma
   * `unit_name` de la invitación.
   */
  start(
    eventId: string,
    slug: string,
    opts: { playerName?: string; invitationToken?: string } = {}
  ): Promise<AttemptStarted> {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      method: 'POST',
      body: opts.playerName ? { player_name: opts.playerName } : {},
      skipAuthHandling: true
    }
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    return apiFetch<AttemptStarted>(
      `/events/${encodeURIComponent(eventId)}/modules/${encodeURIComponent(slug)}/play/start`,
      fetchOpts
    )
  },

  /**
   * `GET /events/:id/modules/:slug/play/attempts` — counter advisory. Lo
   * llama la PWA invitado al montar la tab para mostrar "te quedan N"
   * sin tener que iniciar attempt.
   *
   * Modo B: `attempts_used` y `attempts_remaining` valen `-1` (consultar
   * `gameAttemptsStore`); `phase`/`phase_window_*`/`max_attempts_in_phase`
   * vienen reales (derivan del evento, no del invitado).
   */
  counts(
    eventId: string,
    slug: string,
    opts: { invitationToken?: string } = {}
  ): Promise<AttemptCounts> {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      skipAuthHandling: true,
      noCache: true
    }
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    return apiFetch<AttemptCounts>(
      `/events/${encodeURIComponent(eventId)}/modules/${encodeURIComponent(slug)}/play/attempts`,
      fetchOpts
    )
  }
}

// ─── F009: Módulo Trivia (preguntas) ────────────────────────────────────────

const TRIVIA_SLUG = 'games.trivia'

/**
 * CRUD + import/export de preguntas de Trivia (F009 §4.2/§4.3). El editor
 * (consola admin/cliente) usa JWT; el tótem usa `deviceToken` solo para
 * `list`. Los demás métodos requieren JWT (validado server side por F009 §4.2).
 */
export const triviaQuestionsApi = {
  /**
   * GET preguntas. Auth dual (F021 §5.2): tótem usa `deviceToken`,
   * mobile A usa `invitationToken`, mobile B sin auth. Sin opts → JWT
   * del store (uso de editor en consola).
   */
  list(
    eventId: string,
    opts: { deviceToken?: string; invitationToken?: string } = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {}
    if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    return apiFetch<TriviaQuestionsListResponse>(
      `/events/${eventId}/modules/${TRIVIA_SLUG}/questions`,
      fetchOpts
    )
  },
  create(eventId: string, body: CreateTriviaQuestionRequest) {
    return apiFetch<TriviaQuestion>(
      `/events/${eventId}/modules/${TRIVIA_SLUG}/questions`,
      { method: 'POST', body }
    )
  },
  update(eventId: string, questionId: string, body: UpdateTriviaQuestionRequest) {
    return apiFetch<TriviaQuestion>(
      `/events/${eventId}/modules/${TRIVIA_SLUG}/questions/${questionId}`,
      { method: 'PATCH', body }
    )
  },
  remove(eventId: string, questionId: string) {
    return apiFetch<void>(
      `/events/${eventId}/modules/${TRIVIA_SLUG}/questions/${questionId}`,
      { method: 'DELETE' }
    )
  },
  /**
   * DELETE all (`?force=true`). Sin force el backend responde 400
   * `delete_all_requires_confirmation` — el editor lo envuelve en un
   * `<ConfirmDialog>` antes de llamar.
   */
  removeAll(eventId: string) {
    return apiFetch<void>(
      `/events/${eventId}/modules/${TRIVIA_SLUG}/questions`,
      { method: 'DELETE', query: { force: true } }
    )
  },
  /**
   * Import bulk vía CSV (multipart, F009 §4.3). El backend appendea al set
   * existente, valida fila por fila y devuelve `{ inserted, warnings }`.
   * Para reemplazar el set, llamar `removeAll` antes.
   */
  import(
    eventId: string,
    file: File,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
  ) {
    const fd = new FormData()
    fd.append('file', file)
    const xhrOpts: Parameters<typeof apiUpload>[2] = {}
    if (opts.onProgress) xhrOpts.onProgress = opts.onProgress
    if (opts.signal) xhrOpts.signal = opts.signal
    return apiUpload<ImportQuestionsResponse>(
      `/events/${eventId}/modules/${TRIVIA_SLUG}/questions/import`,
      fd,
      xhrOpts
    )
  },
  /**
   * Export CSV client-side (F009 §5.1). No hay endpoint server-side dedicado —
   * traemos las preguntas con `list()` y armamos el CSV en el browser.
   * Headers iguales a los del import: `question, option_a..d, correct, category`.
   */
  async exportCsv(eventId: string, filename = `trivia-${eventId}.csv`) {
    const res = await this.list(eventId)
    const csv = buildTriviaCsv(res.items)
    // RFC 4180: BOM opcional para que Excel lo abra como UTF-8 sin sorpresas.
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }
}

function buildTriviaCsv(rows: readonly TriviaQuestion[]): string {
  const header = ['question', 'option_a', 'option_b', 'option_c', 'option_d', 'correct', 'category']
  const lines = [header.join(',')]
  for (const r of rows) {
    lines.push(
      [
        r.question,
        r.option_a,
        r.option_b,
        r.option_c,
        r.option_d,
        r.correct,
        r.category ?? ''
      ]
        .map(csvEscape)
        .join(',')
    )
  }
  return lines.join('\r\n')
}

function csvEscape(value: string): string {
  // RFC 4180: si el valor contiene coma, comilla o salto, lo envolvemos en
  // comillas dobles y escapamos las comillas internas duplicándolas.
  if (/[",\r\n]/.test(value)) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}

// ─── F010: Módulo Ruleta + sesión compartida del slot games ─────────────────

const WHEEL_SLUG = 'games.wheel'
const SESSION_SLUG = 'games.session'

/**
 * CRUD de segmentos de la ruleta (F010 §5.3). El editor (consola admin/cliente)
 * usa JWT; el tótem usa `deviceToken` solo para `list` (pre-carga del set
 * antes de animar). `create`/`update`/`remove` son JWT-only.
 */
export const wheelSegmentsApi = {
  /**
   * GET segmentos. Auth dual (F021 §5.2): tótem usa `deviceToken`,
   * mobile A usa `invitationToken`, mobile B sin auth. Sin opts → JWT
   * (editor del cliente).
   */
  list(
    eventId: string,
    opts: { deviceToken?: string; invitationToken?: string } = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {}
    if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
    if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    return apiFetch<WheelSegmentsListResponse>(
      `/events/${eventId}/modules/${WHEEL_SLUG}/segments`,
      fetchOpts
    )
  },
  create(eventId: string, body: CreateWheelSegmentRequest) {
    return apiFetch<WheelSegment>(
      `/events/${eventId}/modules/${WHEEL_SLUG}/segments`,
      { method: 'POST', body }
    )
  },
  update(eventId: string, segmentId: string, body: UpdateWheelSegmentRequest) {
    return apiFetch<WheelSegment>(
      `/events/${eventId}/modules/${WHEEL_SLUG}/segments/${segmentId}`,
      { method: 'PATCH', body }
    )
  },
  remove(eventId: string, segmentId: string) {
    return apiFetch<void>(
      `/events/${eventId}/modules/${WHEEL_SLUG}/segments/${segmentId}`,
      { method: 'DELETE' }
    )
  }
}

/**
 * Spin + listado de wins (F010 §5.4 + §5.5).
 *
 * - `spin` exige `deviceToken` — el server deriva el `device_id` para aplicar
 *   el cooldown de 5s por device. Sin token, 403. El backend responde 409
 *   `no_segments_available` si no hay segmentos elegibles y 429
 *   `cooldown_active` si el mismo device pegó hace <5s.
 * - `wins` sólo es accesible por JWT (admin/cliente/operador asignado). Los
 *   device tokens no pueden leer el log — el tótem no lo necesita.
 */
export const wheelApi = {
  /**
   * POST `/spin`. Auth dual (F010 + F021 §4.4):
   *  - Tótem: `deviceToken` (string legacy, también acepta opts).
   *  - Mobile A: `invitationToken` en `opts`.
   *  - Mobile B: sin auth — `opts` vacío. Backend valida por throttle IP.
   *
   * El body acepta `attempt_id` opcional (F021 §4.4) — mobile lo usa para
   * correlacionar con `event_play_attempts.id`; tótem omite.
   */
  spin(
    eventId: string,
    body: WheelSpinRequest,
    opts: { deviceToken?: string; invitationToken?: string } | string = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {
      method: 'POST',
      body
    }
    if (typeof opts === 'string') {
      // Compat con la firma legacy `(eventId, body, deviceToken)`.
      fetchOpts.deviceToken = opts
    } else {
      if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
      if (opts.invitationToken) fetchOpts.invitationToken = opts.invitationToken
    }
    return apiFetch<WheelSpinResponse>(
      `/events/${eventId}/modules/${WHEEL_SLUG}/spin`,
      fetchOpts
    )
  },
  wins(
    eventId: string,
    params: { limit?: number; offset?: number; since?: string } = {}
  ) {
    return apiFetch<WheelWinsListResponse>(
      `/events/${eventId}/modules/${WHEEL_SLUG}/wins`,
      { query: params }
    )
  }
}

/**
 * Settings del slot games entero (F010 §5.2). No es un módulo jugable — es
 * config compartida del flow del slot (banner IDLE, inactivity timeout).
 * El backend la trata como "módulo virtual" bajo `module_settings.games.session`
 * y la valida con un decoder propio; el heartbeat la incluye en el payload
 * para que el tótem la pueda leer.
 */
export const sessionSettingsApi = {
  get(eventId: string) {
    return apiFetch<SessionSettings>(
      `/events/${eventId}/modules/${SESSION_SLUG}/settings`
    )
  },
  update(eventId: string, body: SessionSettings) {
    return apiFetch<SessionSettings>(
      `/events/${eventId}/modules/${SESSION_SLUG}/settings`,
      { method: 'PATCH', body }
    )
  }
}

// ─── F012: Módulo Fotocabina + slot gallery + galerías públicas ─────────────

const PHOTOBOOTH_SLUG = 'photobooth'

/**
 * Background + overlay del compositor (F012 §4.2). El backend mantiene la
 * regla "1 row de cada `kind` por evento": un POST sobre un kind ya existente
 * lo reemplaza atómicamente. `list` devuelve forma de objeto, no array,
 * porque cada kind es único o ausente.
 */
export const photoboothAssetsApi = {
  list(eventId: string, opts: { deviceToken?: string } = {}) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = {}
    if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
    return apiFetch<PhotoboothAssetsResponse>(
      `/events/${eventId}/modules/${PHOTOBOOTH_SLUG}/assets`,
      fetchOpts
    )
  },
  upload(
    eventId: string,
    kind: PhotoboothAssetKind,
    file: File,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
  ) {
    const fd = new FormData()
    fd.append('file', file)
    const xhrOpts: Parameters<typeof apiUpload>[2] = {}
    if (opts.onProgress) xhrOpts.onProgress = opts.onProgress
    if (opts.signal) xhrOpts.signal = opts.signal
    return apiUpload<PhotoboothAsset>(
      `/events/${eventId}/modules/${PHOTOBOOTH_SLUG}/assets/${kind}`,
      fd,
      xhrOpts
    )
  },
  remove(eventId: string, kind: PhotoboothAssetKind) {
    return apiFetch<void>(
      `/events/${eventId}/modules/${PHOTOBOOTH_SLUG}/assets/${kind}`,
      { method: 'DELETE' }
    )
  }
}

/**
 * Fotos tomadas en la fotocabina (F012 §4.3). El upload requiere device
 * token con slot `photobooth`; el list lo pueden hacer admin/cliente o
 * device con slot `gallery` (consume las fotos para el slideshow). El
 * backend impone cooldown de 2s por device (F012 §4.3).
 */
export const photoboothPhotosApi = {
  list(
    eventId: string,
    opts: {
      limit?: number
      offset?: number
      since?: string
      deviceToken?: string
    } = {}
  ) {
    const fetchOpts: Parameters<typeof apiFetch>[1] = { query: {} }
    if (opts.limit !== undefined) fetchOpts.query!.limit = opts.limit
    if (opts.offset !== undefined) fetchOpts.query!.offset = opts.offset
    if (opts.since !== undefined) fetchOpts.query!.since = opts.since
    if (opts.deviceToken) fetchOpts.deviceToken = opts.deviceToken
    return apiFetch<PhotoboothPhotosListResponse>(
      `/events/${eventId}/modules/${PHOTOBOOTH_SLUG}/photos`,
      fetchOpts
    )
  },
  upload(
    eventId: string,
    file: Blob,
    composed: boolean,
    deviceToken: string,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
  ) {
    const fd = new FormData()
    // El nombre lo pone el backend cuando arma la storage_key — el File de
    // acá es solo wrapper del blob para que el browser lo trate como archivo.
    const file2 =
      file instanceof File
        ? file
        : new File([file], 'photo.png', { type: file.type || 'image/png' })
    fd.append('file', file2)
    fd.append('composed', composed ? 'true' : 'false')
    const xhrOpts: Parameters<typeof apiUpload>[2] = { deviceToken }
    if (opts.onProgress) xhrOpts.onProgress = opts.onProgress
    if (opts.signal) xhrOpts.signal = opts.signal
    return apiUpload<PhotoboothPhoto>(
      `/events/${eventId}/modules/${PHOTOBOOTH_SLUG}/photos`,
      fd,
      xhrOpts
    )
  },
  remove(eventId: string, photoId: string) {
    return apiFetch<void>(
      `/events/${eventId}/modules/${PHOTOBOOTH_SLUG}/photos/${photoId}`,
      { method: 'DELETE' }
    )
  }
}

/**
 * Galerías públicas del cliente (F012 §4.4). Resource del cliente
 * independiente del evento — se asignan con `active_event_id` y el QR
 * estable sobrevive entre eventos.
 */
export const clientGalleriesApi = {
  list(clientId: string) {
    return apiFetch<ClientGalleriesListResponse>(
      `/clients/${clientId}/galleries`
    )
  },
  create(clientId: string, body: ClientGalleryInput) {
    return apiFetch<ClientGallery>(`/clients/${clientId}/galleries`, {
      method: 'POST',
      body
    })
  },
  update(clientId: string, galleryId: string, body: Partial<ClientGalleryInput>) {
    return apiFetch<ClientGallery>(
      `/clients/${clientId}/galleries/${galleryId}`,
      { method: 'PATCH', body }
    )
  },
  remove(clientId: string, galleryId: string) {
    return apiFetch<void>(
      `/clients/${clientId}/galleries/${galleryId}`,
      { method: 'DELETE' }
    )
  },
  /**
   * F012 §4.4: regenera el `token` UUID. Mata QRs viejos. Acción
   * destructiva — el editor envuelve en ConfirmDialog antes de llamar.
   */
  rotate(clientId: string, galleryId: string) {
    return apiFetch<ClientGallery>(
      `/clients/${clientId}/galleries/${galleryId}/rotate`,
      { method: 'POST' }
    )
  }
}

/**
 * Galería pública sin auth (F012 §4.5). NO usa `apiFetch` — endpoint
 * público, sin headers de autenticación. Si el usuario está logueado en
 * la consola y abre la URL pública, no queremos mandar su JWT.
 *
 * 404 → la galería no existe o el token fue rotado. El componente debe
 * detectar el 404 y mostrar la pantalla genérica "Esta galería no existe".
 */
export const publicGalleryApi = {
  async fetch(clientId: string, token: string): Promise<PublicGalleryResponse> {
    const url = `${getApiBaseUrl()}/public/galleries/${clientId}/${token}`
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    if (res.status === 404) {
      throw new PublicGalleryNotFoundError()
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      throw new Error(
        `Galería pública: HTTP ${res.status}${body ? ` — ${body}` : ''}`
      )
    }
    return res.json() as Promise<PublicGalleryResponse>
  },
  /**
   * F025 §4.1: ranking proyectable de la galería pública. Sin auth — el
   * `public_id` autoriza. NO usa `apiFetch` para no mandar JWT de la
   * consola si el cliente está logueado en otra tab (mismo posture que
   * `fetch` arriba).
   *
   * 404 → galería no existe o `public_id` rotó. Componente público
   * decide qué mostrar.
   *
   * El backend rate-limita con el guest limiter. El polling de 10s del
   * frontend no debería trigger limit en uso normal.
   */
  async fetchRanking(
    clientId: string,
    publicId: string,
    opts: { slug?: string; limit?: number } = {}
  ): Promise<PublicRankingResponse> {
    const params = new URLSearchParams()
    if (opts.slug) params.set('slug', opts.slug)
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    const qs = params.toString()
    const url = `${getApiBaseUrl()}/public/galleries/${encodeURIComponent(clientId)}/${encodeURIComponent(publicId)}/ranking${qs ? `?${qs}` : ''}`
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    if (res.status === 404) {
      throw new PublicGalleryNotFoundError()
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      throw new Error(
        `Ranking público: HTTP ${res.status}${body ? ` — ${body}` : ''}`
      )
    }
    return res.json() as Promise<PublicRankingResponse>
  }
}

// ─── F025: Ranking en consola del cliente ──────────────────────────────────

/**
 * `rankingsApi` — endpoints autenticados para el tab "Ranking" del
 * `EventEditShell` (F025 §4.2 + §5.4).
 *
 * Auth: JWT super_admin / cliente owner / operador asignado al evento.
 * El backend valida scope server-side; el frontend confía en el resultado.
 */
export const rankingsApi = {
  /**
   * `GET /events/:id/rankings?slug=...&limit=...` — top N por juego.
   * Sin `slug` devuelve todos los `games.*` activos del evento.
   *
   * El polling del frontend pega a este endpoint cada 10s (default).
   * `noCache: true` para evitar que el HTTP cache del browser devuelva
   * snapshots viejos entre ticks.
   */
  byEvent(
    eventId: string,
    opts: { slug?: string; limit?: number } = {}
  ): Promise<EventRankingsResponse> {
    const query: Record<string, string | number> = {}
    if (opts.slug) query['slug'] = opts.slug
    if (opts.limit !== undefined) query['limit'] = opts.limit
    return apiFetch<EventRankingsResponse>(
      `/events/${encodeURIComponent(eventId)}/rankings`,
      { query, noCache: true }
    )
  },

  /**
   * `DELETE /events/:id/modules/:slug/scores?force=true` — reinicia el
   * ranking de un juego: borra todos sus scores y los intentos de los
   * invitados (vuelven a poder jugar). Solo super_admin; irreversible.
   */
  resetGame(eventId: string, slug: string) {
    return apiFetch<{ deleted_scores: number; reset_attempts: number }>(
      `/events/${encodeURIComponent(eventId)}/modules/${encodeURIComponent(slug)}/scores`,
      { method: 'DELETE', query: { force: 'true' } }
    )
  }
}

/**
 * Error tipado para diferenciar "galería no existe" (404) de errores de red
 * o de servidor — el componente público lo renderiza como pantalla
 * dedicada con branding default Tivy.
 */
export class PublicGalleryNotFoundError extends Error {
  constructor() {
    super('Galería pública no encontrada')
    this.name = 'PublicGalleryNotFoundError'
  }
}

// ─── F013: Fotocabina mobile + moderación ───────────────────────────────────

/**
 * Sube una foto desde el celular del invitado al backend (F013 §4.1 + §5.4).
 *
 * - **Sin auth header** — el endpoint es público, la posesión del
 *   `(client_id, token)` ya autoriza.
 * - Usa `XMLHttpRequest` para tener `xhr.upload.onprogress` (fetch no
 *   reporta progreso de upload). El callback `onProgress(fraction)` recibe
 *   valores 0..1 mientras el body sube.
 * - Timeout 30 s — el backend procesa el upload sincrónicamente (sube a S3 +
 *   inserta row + encola al worker), debería responder en <2 s.
 * - Cualquier respuesta non-2xx → throw `Error` genérico. El invitado nunca
 *   ve códigos distintos según el resultado real (decisión de UX
 *   §5.2/§4.1).
 */
export const mobilePhotoboothApi = {
  upload(
    clientId: string,
    token: string,
    blob: Blob,
    opts: {
      playerName?: string
      onProgress?: (fraction: number) => void
      signal?: AbortSignal
    } = {}
  ): Promise<MobileUploadResponse> {
    const url = `${getApiBaseUrl()}/public/galleries/${clientId}/${token}/uploads`
    const fd = new FormData()
    const file =
      blob instanceof File
        ? blob
        : new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' })
    fd.append('file', file)
    const trimmed = opts.playerName?.trim()
    if (trimmed) fd.append('player_name', trimmed)

    return new Promise<MobileUploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      xhr.responseType = 'text'
      xhr.timeout = 30_000
      xhr.setRequestHeader('Accept', 'application/json')
      // ⚠️ No `Authorization` ni `X-Device-Token` — endpoint público.

      if (opts.onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            opts.onProgress!(e.loaded / e.total)
          }
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let parsed: MobileUploadResponse | null = null
          try {
            parsed = JSON.parse(xhr.responseText) as MobileUploadResponse
          } catch {
            // Ignore — el backend siempre devuelve JSON pero por las dudas
            // resolvemos con un shape válido si el body está vacío.
          }
          resolve(parsed ?? { status: 'queued', message: '' })
          return
        }
        // Error genérico — el invitado no debe ver el código real.
        reject(new MobilePhotoboothUploadError())
      }

      xhr.ontimeout = () => reject(new MobilePhotoboothUploadError())
      xhr.onerror = () => reject(new MobilePhotoboothUploadError())
      xhr.onabort = () => reject(new MobilePhotoboothUploadError())

      if (opts.signal) {
        if (opts.signal.aborted) {
          xhr.abort()
          return
        }
        opts.signal.addEventListener('abort', () => xhr.abort())
      }

      xhr.send(fd)
    })
  }
}

/**
 * Error genérico del upload mobile. NO expone status ni código del backend —
 * todos los errores se ven igual para el invitado (F013 §4.1: "mensaje
 * genérico de respuesta — independiente del resultado real").
 */
export class MobilePhotoboothUploadError extends Error {
  constructor() {
    super('Algo salió mal, probá de nuevo')
    this.name = 'MobilePhotoboothUploadError'
  }
}

/**
 * Fotos rechazadas por el filtro NSFW (F013 §4.3). Endpoints con JWT —
 * **nunca** accesibles desde token público. El cliente owner del evento
 * puede aprobar (re-encolar al worker con flag de skip-NSFW) o borrar
 * inmediatamente sin esperar al TTL de 7 días.
 */
export const photoboothRejectedApi = {
  async list(eventId: string): Promise<PhotoboothRejectedUpload[]> {
    const res = await apiFetch<PhotoboothRejectedListResponse>(
      `/events/${eventId}/photobooth/rejected`
    )
    return res.items
  },
  approve(eventId: string, uploadId: string) {
    return apiFetch<{ status: 'queued' }>(
      `/events/${eventId}/photobooth/rejected/${uploadId}/approve`,
      { method: 'POST' }
    )
  },
  remove(eventId: string, uploadId: string) {
    return apiFetch<void>(
      `/events/${eventId}/photobooth/rejected/${uploadId}`,
      { method: 'DELETE' }
    )
  }
}

/**
 * Capabilities del sistema (F013 §5.6). El editor del cliente la consume
 * para decidir si mostrar el warning de NSFW passthrough. Si el endpoint
 * no existe (404 — backend pre-F013), devolvemos `null` y el caller no
 * muestra warning ni positivo ni negativo.
 */
export const systemApi = {
  async capabilities(): Promise<SystemCapabilities | null> {
    try {
      return await apiFetch<SystemCapabilities>('/system/info')
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return null
      }
      throw err
    }
  }
}

// ─── B6 GlobalSearch (super admin) ──────────────────────────────────────

/**
 * Resultado individual del typeahead de búsqueda global del admin
 * (B6 reform consolas, 2026-05-08). Shape común para clients/events/
 * operators — el frontend renderiza con un ícono distinto por `kind`.
 */
export interface SearchResult {
  kind: 'client' | 'event' | 'operator'
  id: string
  label: string
  sub: string
}

interface SearchResponse {
  results: SearchResult[]
}

/**
 * Búsqueda global para super admin. Cap server-side de ~30 resultados,
 * sin paginación (es typeahead). Match prefix + substring case-insensitive
 * sobre name/email del backend.
 */
export const adminApi = {
  async search(q: string, limit = 20): Promise<SearchResult[]> {
    const trimmed = q.trim()
    if (trimmed.length === 0) return []
    const res = await apiFetch<SearchResponse>('/admin/search', {
      query: { q: trimmed, limit }
    })
    return res.results
  }
}

/**
 * F029 — resuelve un link de Google Maps (corto de "Compartir" o completo) a
 * coordenadas. El backend sigue el redirect (los cortos no traen las coords
 * en el path).
 */
export const mapsApi = {
  resolve(url: string) {
    return apiFetch<{ lat: number; lng: number }>('/maps/resolve', {
      method: 'POST',
      body: { url }
    })
  }
}
